import { animate, stagger, inView } from 'motion';
import SplitType from 'split-type';
import { prefersReducedMotion } from './index';

// Track which titles have been initialized to prevent duplicate setup
const initializedTitles = new WeakSet();

// Track which titles are currently being animated to prevent multiple animations
const animatingTitles = new WeakSet();

export const initTitlesAnimation = () => {
  const modules = Array.from(document?.querySelectorAll('.module:not(.header,.footer,.rtf)') || []);
  const modulesWithTitles = modules?.filter(module => module.querySelectorAll('h1,h2').length > 0);
  const titles = modulesWithTitles
    ?.map(module => Array.from(module.querySelectorAll('h1,h2')))
    .flat();

  if (prefersReducedMotion()) {
    return;
  }

  titles?.forEach(title => {
    const titleEl = title as HTMLElement;

    // Preserve responsive line breaks and accessible heading text in these page templates.
    if (
      titleEl.closest('.logisnext-forklifts, .industries-page') ||
      titleEl.matches('.stats-counter')
    )
      return;

    // Skip if this title has already been initialized
    if (initializedTitles.has(titleEl)) {
      return;
    }

    // Mark title as initialized
    initializedTitles.add(titleEl);

    // Skip if title is already animated and visible
    if (titleEl.dataset.titleAnimated === 'true') {
      return;
    }

    const { lines } = new SplitType(titleEl, {
      tagName: 'span'
    });

    if (lines && lines.length > 0) {
      // Use inView on the parent title element, not the lines array
      inView(
        titleEl,
        () => {
          // Skip if title is already being animated
          if (animatingTitles.has(titleEl)) {
            return;
          }

          // Skip if already animated
          if (titleEl.dataset.titleAnimated === 'true') {
            return;
          }

          setTimeout(() => {
            startTitleAnimation();
          }, 50);

          function startTitleAnimation() {
            // Double-check we're not already animating
            if (animatingTitles.has(titleEl) || titleEl.dataset.titleAnimated === 'true') {
              return;
            }

            // Ensure lines still exist
            if (!lines || lines.length === 0) {
              return;
            }

            // Mark as being animated
            animatingTitles.add(titleEl);

            // Set initial state for lines
            lines.forEach(line => {
              (line as HTMLElement).style.opacity = '0';
              (line as HTMLElement).style.transform = 'translateY(100%)';
            });

            // Animate the lines
            animate(
              lines as HTMLElement[],
              { opacity: [0, 1], y: ['100%', '0%'] },
              {
                delay: stagger(0.05),
                duration: 0.3,
                ease: 'easeInOut',
                onComplete: () => {
                  titleEl.dataset.titleAnimated = 'true';
                  animatingTitles.delete(titleEl);
                }
              }
            );
          }
        },
        { margin: '0px 0px -20% 0px' }
      );
    }
  });
};
